// createVideoElement.js
'use strict';

import { player as playerDebug } from '../resource/debug.js';
import { LoadMode, PRIORITY_BY_LOAD_MODE } from '../resource/playerConfig.js';
import { createFireCallback } from '../resource/playerLogUtils.js';
import {
  LifecycleState,
  ACQUIRE_TIMEOUT_MS,
  INITIAL_PLAYBACK_STATE,
} from '../resource/createPlayer.js';

import raceWithTimeout from '../resource/raceWithTimeout.js';

const nativeLog = playerDebug.extend('log:createVideoElement');

let _instanceCounter = 0;
const generateInternalId = () => `native-${++_instanceCounter}`;

/** Native <video> player with createPlayer-shaped API. MP4 progressive only, no adapter. */
export default function createVideoElement({
  videoElementPool,
  source: initialSource,
  priority,
  playerContainerRef = null,
  debugLabel,
  onEnded = null,
  onPlaying = null,
  onPause = null,
  onWaiting = null,
  onCanPlay = null,
  onProgressUpdate = null,
  onError = null,
  onLoadStart = null,
  onLoadedData = null,
  onLoadedMetadata = null,
  onSeeking = null,
  onSeeked = null,
  onProgress = null,
  onTimeupdate = null,
  onVolumeChange = null,
  onCanplaythrough = null,
  onEnterPictureInPicture = null,
  onPreempted = null,
  shouldLoop = () => false,
}) {
  if (!videoElementPool) {
    throw new Error('createVideoElement: videoElementPool is required');
  }

  const internalId = generateInternalId();
  let lifecycle = LifecycleState.IDLE;
  let playback = { ...INITIAL_PLAYBACK_STATE };
  let videoElement = null;
  let isReleaseRequested = false;
  let currentPriority = priority;
  const abortController = new AbortController();
  const subscribers = new Set();
  let eventCleanups = [];

  const log = (message, data) => {
    if (data === undefined) nativeLog(`(${internalId}) ${message}`);
    else nativeLog(`(${internalId}) ${message}`, data);
  };

  const fireCallback = createFireCallback(log);

  const notifySubscribers = () => {
    for (const subscriber of subscribers) {
      try {
        subscriber(playback);
      } catch (_) {} // eslint-disable-line no-empty
    }
  };

  const updatePlayback = update => {
    if (!update) return;
    const previous = playback;
    playback = { ...playback, ...update };
    notifySubscribers();

    const timeChanged =
      playback.currentTime !== previous.currentTime ||
      playback.duration !== previous.duration;
    if (
      timeChanged &&
      !(playback.currentTime === 0 && playback.duration === 0)
    ) {
      fireCallback(onProgressUpdate, 'onProgressUpdate', {
        currentTime: playback.currentTime,
        duration: playback.duration,
      });
    }
  };

  const cleanupEvents = () => {
    for (const cleanup of eventCleanups) cleanup();
    eventCleanups = [];
  };

  const wireVideoEvents = element => {
    const listen = (event, handler) => {
      element.addEventListener(event, handler);
      eventCleanups.push(() => element.removeEventListener(event, handler));
    };

    listen('canplay', () => {
      updatePlayback({ hasReceived: true });
      fireCallback(onCanPlay, 'onCanPlay');
    });
    listen('playing', () => {
      updatePlayback({ isPlaying: true, isStalled: false, hasReceived: true });
      fireCallback(onPlaying, 'onPlaying', { type: 'playing' });
    });
    listen('pause', () => {
      // Native <video> fires pause before ended; suppress to avoid double callback.
      if (element.ended) return;
      updatePlayback({ isPlaying: false });
      fireCallback(onPause, 'onPause');
    });
    listen('ended', () => {
      updatePlayback({ isPlaying: false, hasReceived: true });
      fireCallback(onEnded, 'onEnded');
    });
    listen('waiting', () => {
      updatePlayback({ isStalled: true });
      fireCallback(onWaiting, 'onWaiting');
    });
    listen('timeupdate', () => {
      updatePlayback({
        currentTime: element.currentTime,
        duration: element.duration || 0,
      });
      fireCallback(onTimeupdate, 'onTimeupdate', {
        type: 'timeupdate',
        target: element,
      });
    });
    listen('loadedmetadata', () => {
      updatePlayback({ duration: element.duration || 0 });
      fireCallback(onLoadedMetadata, 'onLoadedMetadata', {
        type: 'loadedmetadata',
        target: element,
      });
    });
    listen('error', () => {
      const mediaError = element.error;
      updatePlayback({ isPlaying: false, error: mediaError });
      fireCallback(onError, 'onError', mediaError);
    });

    if (onLoadStart)
      listen('loadstart', event =>
        fireCallback(onLoadStart, 'onLoadStart', event)
      );
    if (onLoadedData)
      listen('loadeddata', event =>
        fireCallback(onLoadedData, 'onLoadedData', event)
      );
    if (onSeeking)
      listen('seeking', event => fireCallback(onSeeking, 'onSeeking', event));
    if (onSeeked)
      listen('seeked', event => fireCallback(onSeeked, 'onSeeked', event));
    if (onProgress)
      listen('progress', event =>
        fireCallback(onProgress, 'onProgress', event)
      );
    if (onVolumeChange)
      listen('volumechange', event =>
        fireCallback(onVolumeChange, 'onVolumeChange', event)
      );
    if (onCanplaythrough)
      listen('canplaythrough', event =>
        fireCallback(onCanplaythrough, 'onCanplaythrough', event)
      );
    if (onEnterPictureInPicture)
      listen('enterpictureinpicture', event =>
        fireCallback(onEnterPictureInPicture, 'onEnterPictureInPicture', event)
      );
  };

  const handlePreempted = () => {
    log('preempted');
    cleanupEvents();
    playback = { ...INITIAL_PLAYBACK_STATE };
    videoElement = null;
    if (isReleaseRequested) return;
    lifecycle = LifecycleState.IDLE;
    notifySubscribers();
    fireCallback(onPreempted, 'onPreempted');
  };

  function setPriority(nextPriority) {
    if (nextPriority === currentPriority || isReleaseRequested) return;
    currentPriority = nextPriority;
    videoElementPool.updatePriority?.(internalId, nextPriority);
  }

  function load() {
    if (isReleaseRequested || lifecycle !== LifecycleState.IDLE) {
      return Promise.resolve();
    }

    lifecycle = LifecycleState.LOADING;
    log('load:start', { sourceId: initialSource?.id });

    return raceWithTimeout({
      promise: videoElementPool.acquire(internalId, {
        priority: currentPriority,
        debugNo: internalId,
        onPreempted: handlePreempted,
        signal: abortController.signal,
        onBeforeRelease: cleanupEvents,
      }),
      ms: ACQUIRE_TIMEOUT_MS,
      label: 'videoElementPool.acquire (native)',
    })
      .then(element => {
        if (isReleaseRequested || abortController.signal.aborted) {
          videoElementPool.release(internalId).catch(() => {});
          lifecycle = LifecycleState.DESTROYED;
          return;
        }

        videoElement = element;
        playerContainerRef?.current?.appendChild(element);
        wireVideoEvents(element);

        const mp4Url = initialSource?.src?.mp4;
        if (mp4Url) element.src = mp4Url;
        element.loop = shouldLoop();

        lifecycle = LifecycleState.READY;
        notifySubscribers();
        log('load:done', { videoElementId: element.id });
      })
      .catch(error => {
        videoElementPool.release(internalId).catch(() => {});
        lifecycle = LifecycleState.IDLE;
        if (error?.name === 'AbortError') {
          log('load:aborted');
          return;
        }
        log('load:failed', { error: error?.message || String(error) });
        throw error;
      });
  }

  function play() {
    if (
      lifecycle !== LifecycleState.READY &&
      lifecycle !== LifecycleState.ACTIVE
    ) {
      return Promise.resolve(false);
    }
    if (!videoElement) return Promise.resolve(false);
    lifecycle = LifecycleState.ACTIVE;
    return videoElement
      .play()
      .then(() => true)
      .catch(error => {
        log('play:threw', { error: error?.message || String(error) });
        throw error;
      });
  }

  function pause() {
    if (!videoElement) return;
    try {
      videoElement.pause();
    } catch (_) {} // eslint-disable-line no-empty
  }

  function seek(time) {
    if (!videoElement) return;
    videoElement.currentTime = time;
    updatePlayback({ currentTime: time });
  }

  function release() {
    if (isReleaseRequested) return Promise.resolve();
    isReleaseRequested = true;
    abortController.abort();
    cleanupEvents();

    videoElement = null;
    lifecycle = LifecycleState.DESTROYED;

    return videoElementPool.release(internalId).catch(error => {
      log('release:threw', { error: error?.message || String(error) });
    });
  }

  function update({ loadMode: nextLoadMode } = {}) {
    if (nextLoadMode === undefined) return;
    setPriority(PRIORITY_BY_LOAD_MODE[nextLoadMode] ?? currentPriority);

    if (nextLoadMode === LoadMode.ACTIVE) {
      if (lifecycle === LifecycleState.IDLE) load().catch(() => {});
      else if (lifecycle === LifecycleState.READY) play().catch(() => {});
    } else if (
      nextLoadMode !== LoadMode.ACTIVE &&
      lifecycle === LifecycleState.ACTIVE
    ) {
      pause();
    }
  }

  function subscribe(callback) {
    if (typeof callback !== 'function') return () => {};
    subscribers.add(callback);
    return () => subscribers.delete(callback);
  }

  return {
    load,
    play,
    pause,
    seek,
    reload: () => Promise.resolve(),
    release,
    subscribe,
    getSnapshot: () => playback,
    isMuted: () => videoElement?.muted ?? true,
    setMuted: muted => {
      if (videoElement) videoElement.muted = muted;
    },
    getVideoElement: () => videoElement,
    invalidateLoadedSource: () => {},
    setPriority,
    update,
    get id() {
      return debugLabel || internalId;
    },
    get state() {
      return lifecycle;
    },
    get debugNo() {
      return internalId;
    },
  };
}
